(() => {
  const config = window.CyberMonkeyTheme || {};
  const root = () => window.Shopify?.routes?.root || config.rootUrl || '/';
  const endpoint = (name, fallback) => `${root()}${name}`.replace(/([^:]\/)\/+/, '$1');
  const money = (cents, currency) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, config.moneyFormat);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
  };
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || drawer.dataset.initialized) return;
    drawer.dataset.initialized = 'true';
    const panel = drawer.querySelector('.cart-drawer__panel');
    const itemsContainer = drawer.querySelector('[data-cart-items]');
    const footer = drawer.querySelector('[data-cart-footer]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    const live = drawer.querySelector('[data-cart-live]');
    const shipping = drawer.querySelector('[data-free-shipping]');
    let returnFocus = null;
    let pending = false;

    const focusable = () => [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')];
    const announce = (message) => { live.textContent = ''; window.setTimeout(() => { live.textContent = message; }, 20); };
    const setBusy = (busy) => {
      pending = busy;
      panel.setAttribute('aria-busy', String(busy));
      panel.querySelectorAll('button').forEach((button) => { button.disabled = busy; });
    };
    const updateBubble = (count) => {
      document.querySelectorAll('[data-cart-count]').forEach((bubble) => { bubble.textContent = count; });
      document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => { trigger.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`); });
    };
    const updateShipping = (cart) => {
      if (!shipping) return;
      const threshold = Number(shipping.dataset.threshold || 0);
      const message = shipping.querySelector('[data-free-shipping-message]');
      const progress = shipping.querySelector('[data-free-shipping-progress]');
      const remaining = Math.max(0, threshold - cart.total_price);
      progress.style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
      message.textContent = remaining > 0 ? `${money(remaining, cart.currency)} away from free shipping on qualifying orders.` : 'Free shipping unlocked for qualifying orders.';
    };
    const emptyState = () => {
      const empty = make('div', 'cart-drawer__empty');
      const image = make('img');
      image.src = drawer.dataset.emptyImage;
      image.alt = 'Slim from the Cyber Monkey crew making a silly face';
      image.width = 720;
      image.height = 720;
      image.loading = 'lazy';
      const eyebrow = make('p', 'cart-drawer__eyebrow', 'Cart status');
      const heading = make('h3', '', 'Your cart is looking suspiciously banana-free.');
      const link = make('a', 'cyber-button cyber-button--primary', 'Continue shopping');
      link.href = `${root()}collections/all`;
      empty.append(image, eyebrow, heading, link);
      return empty;
    };
    const lineItem = (item) => {
      const line = make('li', 'cart-drawer__item');
      if (item.image) {
        const image = make('img', 'cart-drawer__image');
        image.src = item.image;
        image.alt = item.product_title || item.title;
        image.width = 160;
        image.height = 160;
        image.loading = 'lazy';
        line.append(image);
      }
      const details = make('div', 'cart-drawer__item-details');
      const link = make('a', '', item.product_title || item.title);
      link.href = item.url;
      details.append(link);
      if (item.variant_title && item.variant_title !== 'Default Title') details.append(make('p', '', item.variant_title));
      details.append(make('p', 'cart-drawer__line-price', money(item.final_line_price, item.currency)));
      const quantity = make('div', 'cart-drawer__quantity');
      quantity.setAttribute('aria-label', `Quantity for ${item.product_title || item.title}`);
      [['decrease', '−', 'Decrease'], ['increase', '+', 'Increase']].forEach(([action, character, verb], index) => {
        const button = make('button', '', character);
        button.type = 'button';
        button.dataset.cartChange = action;
        button.dataset.lineKey = item.key;
        button.setAttribute('aria-label', `${verb} quantity for ${item.product_title || item.title}`);
        if (index === 0) quantity.append(button, make('span', '', String(item.quantity)));
        else quantity.append(button);
      });
      details.append(quantity);
      const remove = make('button', 'cart-drawer__remove', 'Remove');
      remove.type = 'button';
      remove.dataset.cartRemove = '';
      remove.dataset.lineKey = item.key;
      details.append(remove);
      line.append(details);
      return line;
    };
    const render = (cart) => {
      itemsContainer.replaceChildren();
      if (cart.item_count) {
        const list = make('ul', 'cart-drawer__items');
        list.setAttribute('role', 'list');
        cart.items.forEach((item) => list.append(lineItem(item)));
        itemsContainer.append(list);
        footer.hidden = false;
        subtotal.textContent = money(cart.total_price, cart.currency);
      } else {
        itemsContainer.append(emptyState());
        footer.hidden = true;
      }
      updateBubble(cart.item_count);
      updateShipping(cart);
    };
    const fetchCart = async () => {
      const response = await fetch(endpoint('cart.js'), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not load cart');
      return response.json();
    };
    const change = async (key, quantity) => {
      if (pending) return;
      setBusy(true);
      try {
        const response = await fetch(endpoint('cart/change.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
        if (!response.ok) throw new Error('Could not update cart');
        const cart = await response.json();
        render(cart);
        announce('Cart updated.');
      } catch (error) { announce('The cart could not be updated. Please try again.'); }
      finally { setBusy(false); }
    };
    const close = () => {
      drawer.classList.remove('is-open');
      document.body.classList.remove('is-scroll-locked');
      window.setTimeout(() => {
        drawer.hidden = true;
        if (returnFocus) returnFocus.focus();
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200);
    };
    const open = async (trigger) => {
      returnFocus = trigger || document.activeElement;
      drawer.hidden = false;
      document.body.classList.add('is-scroll-locked');
      window.requestAnimationFrame(() => drawer.classList.add('is-open'));
      panel.focus();
      try { render(await fetchCart()); } catch (error) { announce('The cart could not be refreshed.'); }
    };
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-drawer-trigger]');
      if (trigger) { event.preventDefault(); open(trigger); return; }
      if (event.target.closest('[data-cart-drawer-close]')) close();
      const control = event.target.closest('[data-cart-change], [data-cart-remove]');
      if (!control || pending) return;
      const key = control.dataset.lineKey;
      if (control.hasAttribute('data-cart-remove')) { change(key, 0); return; }
      const current = Number(control.parentElement.querySelector('span').textContent);
      change(key, control.dataset.cartChange === 'increase' ? current + 1 : Math.max(0, current - 1));
    });
    document.addEventListener('keydown', (event) => {
      if (drawer.hidden) return;
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab') return;
      const targets = focusable();
      if (!targets.length) return;
      const first = targets[0];
      const last = targets[targets.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form || form.dataset.noAjax === 'true') return;
      event.preventDefault();
      const submitter = event.submitter;
      if (submitter) submitter.disabled = true;
      const errorTarget = form.querySelector('[data-form-error]');
      if (errorTarget) errorTarget.textContent = '';
      try {
        const response = await fetch(endpoint('cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) {
          const payload = await response.json();
          throw new Error(payload.description || 'Could not add this item to your cart.');
        }
        await open(submitter || form);
        announce('Item added to your cart.');
      } catch (error) {
        if (errorTarget) errorTarget.textContent = error.message;
        else announce(error.message);
      } finally { if (submitter) submitter.disabled = false; }
    });
  }
  document.addEventListener('DOMContentLoaded', initCartDrawer);
  document.addEventListener('shopify:section:load', initCartDrawer);
})();
